import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import Helmet from 'react-helmet';
import { useSubscriptions } from './apiHooks';
import Loader from '../../components/loader';
import ErrorMessage from '../../components/errorMessage';
import utils from '../../utils/utils';
import CustomPaginator from '../../components/paginator';
import { CSVLink } from 'react-csv';
import AdminSubscriptionsTable from './adminSubscriptionsTable';
import DATE_FORMAT from '../../components/constants/dateFormat';
import DateField from '../../components/customFields/dateField';
import CustomSelect from '../../components/customFields/customSelect';

const RESULTS_PER_PAGE = 350;

const AdminSubscriptions = () => {
  const [sortBy, setSortBy] = useState('idDesc');
  const [activePage, setActivePage] = useState(1);
  const [showArchived, setShowArchived] = useState(false);
  const [showActive, setShowActive] = useState(true);
  const [deliveryDay, setDeliveryDay] = useState(null);
  const [timeRange, setTimeRange] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [nextDeliveryDateFrom, setNextDeliveryDateFrom] = useState(null);
  const [nextDeliveryDateTo, setNextDeliveryDateTo] = useState(null);
  const [itemName, setItemName] = useState(null);
  const [frequency, setFrequency] = useState(null);

  const {
    isLoading: loading,
    result,
    isError,
    error,
  } = useSubscriptions({
    sortBy,
    max: RESULTS_PER_PAGE,
    offset: (activePage - 1) * RESULTS_PER_PAGE,
    admin: true,
    showInactive,
    showActive,
    showArchived,
    deliveryDay,
    timeRange,
    nextDeliveryDateFrom,
    nextDeliveryDateTo,
    itemName,
    frequency,
  });

  function setSortMethod(newSortBy) {
    let sortParam;

    switch (newSortBy) {
      case 'id':
      default:
        sortParam = sortBy === 'idAsc' ? 'idDesc' : 'idAsc';
    }

    setSortBy(sortParam);
  }

  const { formatMessage } = useIntl();

  const paginator = (
    <div className="m-b-3 m-t-3">
      <CustomPaginator
        activePage={activePage}
        resultsPerPage={RESULTS_PER_PAGE}
        setActivePage={(newNumber) => {
          if (newNumber !== activePage) {
            setActivePage(newNumber);
          }
        }}
        totalCount={result?.totalCount || 0}
      />
      <div>Total count: {result?.totalCount || '-'}</div>
    </div>
  );

  return (
    <div>
      <Helmet>
        <title>{formatMessage({ id: 'admin.subscriptions.title' })}</title>
      </Helmet>
      <h3>
        <FormattedMessage id={'admin.subscriptions.title'} />
      </h3>

      <Loader show={loading} />
      {isError ? <ErrorMessage error={utils.getErrorMessageFromResponse(error)} /> : ''}

      <div className="m-b-3">
        <Link className="btn btn-default m-r-2" to="/admin/subscriptions/create">
          <FormattedMessage id={'admin.button.create'} />
        </Link>
        <CSVLink
          className="btn btn-default m-r-2"
          data={getCsvData(result?.subscriptions)}
          filename={`membo-subscriptions.csv`}
        >
          Download CSV
        </CSVLink>
      </div>

      <div className="m-b-2">
        {[
          {
            label: 'Only active',
            onClick: () => {
              if (!showActive) {
                setShowInactive(false);
                setShowArchived(false);
              }
              setShowActive(!showActive);
            },
            isSelected: showActive,
          },
          {
            label: 'Only inactive',
            onClick: () => {
              if (!showInactive) {
                setShowActive(false);
              }
              setShowInactive(!showInactive);
            },
            isSelected: showInactive,
          },
          {
            label: 'Only archived',
            onClick: () => {
              if (!showArchived) {
                setShowActive(false);
              }
              setShowArchived(!showArchived);
            },
            isSelected: showArchived,
          },
          {
            label: 'WEEKLY',
            onClick: () => {
              setFrequency(frequency === 'WEEKLY' ? null : 'WEEKLY');
            },
            isSelected: frequency === 'WEEKLY',
          },
          {
            label: 'BI_WEEKLY',
            onClick: () => {
              setFrequency(frequency === 'BI_WEEKLY' ? null : 'BI_WEEKLY');
            },
            isSelected: frequency === 'BI_WEEKLY',
          },
          {
            label: 'MONDAY',
            onClick: () => (deliveryDay === 'MONDAY' ? setDeliveryDay(null) : setDeliveryDay('MONDAY')),
            isSelected: deliveryDay === 'MONDAY',
          },
          {
            label: 'FRIDAY',
            onClick: () => (deliveryDay === 'FRIDAY' ? setDeliveryDay(null) : setDeliveryDay('FRIDAY')),
            isSelected: deliveryDay === 'FRIDAY',
          },
          ...['12:00-15:00', '15:00-18:00', '18:00-21:00'].map((newTimeRange) => ({
            label: newTimeRange,
            onClick: () => (newTimeRange === timeRange ? setTimeRange(null) : setTimeRange(newTimeRange)),
            isSelected: newTimeRange === timeRange,
          })),
        ].map((badge) => (
          <span
            className={`badge p-a-2 m-r-2 m-b-2 ${badge.isSelected ? 'badge-primary' : 'badge-secondary'}`}
            onClick={badge.onClick}
          >
            {badge.label}
          </span>
        ))}
      </div>

      <div className="row">
        <div className="col-12 col-sm-4 col-md-3">
          <div className="form-group">
            <DateField
              onChange={(newValue) => setNextDeliveryDateFrom(newValue)}
              schema={{
                title: 'Next delivery from',
              }}
              formData={nextDeliveryDateFrom}
            />
          </div>
        </div>
        <div className="col-12 col-sm-4 col-md-3">
          <div className="form-group">
            <DateField
              onChange={(newValue) => setNextDeliveryDateTo(newValue)}
              schema={{
                title: 'Next delivery To',
              }}
              formData={nextDeliveryDateTo}
            />
          </div>
        </div>
        <div className="col-12 col-sm-4 col-md-3">
          <div className="form-group">
            <CustomSelect
              onChange={(value) => setItemName(value)}
              schema={{
                title: 'Item',
                options: [
                  {
                    label: 'Fruit box',
                    value: 'puuviljakast',
                  },
                  {
                    label: 'Veg box',
                    value: 'köögiviljakast',
                  },
                  {
                    label: 'Orange box',
                    value: 'apelsini kast',
                  },
                  {
                    label: 'Office box',
                    value: 'viljakast kontoritele',
                  },
                ],
                showClearOption: true,
              }}
              formData={itemName}
            />
          </div>
        </div>
      </div>

      {paginator}

      <AdminSubscriptionsTable subscriptions={result?.subscriptions} setSortMethod={setSortMethod} />

      {paginator}
    </div>
  );
};

function getCsvData(subscriptions) {
  if (!subscriptions) {
    return '';
  }
  const csvHeaders = [
    'Name',
    'Address',
    'Unit',
    'Lat',
    'Lon',
    'Phone',
    'Email',
    'Delivery time',
    'Order content',
    'Next delivery',
  ];

  return [
    csvHeaders,
    ...subscriptions.map((subscription) => {
      const unit = subscription.address.unit || '';
      const id = `S${subscription.id}`;

      const items = subscription.items
        .map((subscriptionItem) => {
          return `${
            subscriptionItem.quantity > 1 ? `${subscriptionItem.quantity} x ` : ''
          }${subscriptionItem.itemName.replaceAll('"', "'")}`;
        })
        .join('\n');

      return [
        `${id} - ${subscription.address.customerName}`,
        subscription.address.street,
        unit,
        subscription.address.coordinates ? subscription.address.coordinates.lat : '',
        subscription.address.coordinates ? subscription.address.coordinates.lon : '',
        subscription.address.phoneNumber,
        subscription.address.email,
        subscription.timeRange,
        `${items}`,
        `${utils.getFormattedDateFromString(subscription.nextDeliveryDate, DATE_FORMAT.displayWeekdayAndDate)}`,
      ];
    }),
  ];
}

export default AdminSubscriptions;
